import type { Notification } from 'types';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useClient } from 'hooks/useClient';

// Matches the cron cadence that produces most of these. Polling faster buys
// nothing because there is nothing new to find between runs.
const POLL_INTERVAL = 60_000;

type NotificationPage = {
  notifications: Notification[];
  nextCursor: number | null;
};

export const useUnreadCount = (enabled = true) => {
  const client = useClient();

  const { data } = useQuery<{ count: number }>({
    queryKey: ['NOTIFICATIONS_UNREAD'],
    queryFn: () => client.get<{ count: number }>('/notifications/unread-count'),
    refetchInterval: POLL_INTERVAL,
    refetchOnWindowFocus: true,
    staleTime: POLL_INTERVAL,
    enabled,
  });

  return data?.count ?? 0;
};

/**
 * The feed itself. Only fetched while the panel is open: the badge is what
 * polls, and it costs one indexed count rather than a page of joined rows.
 */
export const useNotifications = (isOpen: boolean) => {
  const client = useClient();
  const queryClient = useQueryClient();

  const query = useInfiniteQuery<NotificationPage>({
    queryKey: ['NOTIFICATIONS'],
    queryFn: ({ pageParam }) => {
      const cursor = pageParam ? `?cursor=${pageParam}` : '';
      return client.get<NotificationPage>(`/notifications${cursor}`);
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: isOpen,
  });

  const markSeen = useMutation({
    mutationFn: () => client.post('/notifications/seen'),
    onSuccess: () => {
      // The badge is the thing being cleared, so update it directly rather than
      // waiting out the poll interval.
      queryClient.setQueryData(['NOTIFICATIONS_UNREAD'], { count: 0 });
    },
  });

  const markRead = useMutation({
    mutationFn: (payload: { ids?: number[]; all?: boolean }) => client.post('/notifications/read', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['NOTIFICATIONS'] });
      queryClient.setQueryData(['NOTIFICATIONS_UNREAD'], { count: 0 });
    },
  });

  const notifications = query.data?.pages.flatMap((page) => page.notifications) ?? [];

  return {
    notifications,
    isLoading: query.isLoading,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    markSeen: markSeen.mutate,
    markRead: markRead.mutate,
  };
};
